import React, { useState, useEffect, useMemo } from 'react';
import { User, Check, CheckCircle2, Lock } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { allCountries, civilities } from '../../data/quoteFormOptions';
import { getRegionOptions, getVilleOptions } from '../../data/geoData';
import DashModal from './DashModal';
import PasswordChangeForm from './PasswordChangeForm';
import ClientRegisterForm from '../ClientRegisterForm';
import './DashForm.css';
import '../../pages/AuthPage.css';

export default function ProfileForm({ space, onBack, user: userProp, onSave, hidePassword }) {
  const { t } = useLanguage();
  const { user: authUser, updateProfile } = useAuth();
  const user = userProp || authUser;
  const isClient = space === 'client';
  const [form, setForm] = useState({
    civility: user?.civility || '',
    name: user?.name || '',
    phone: user?.phone || '',
    city: user?.city || '',
    region: user?.region || '',
    country: user?.country || '',
    clientType: user?.clientType || 'particulier',
    organizationName: user?.organizationName || '',
    taxId: user?.taxId || '',
    contactMode: user?.contactMode || 'email',
  });
  const [saved, setSaved] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched) return;
    if (!form.name.trim()) {
      setError(t.auth.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
    } else {
      setError('');
    }
  }, [touched, form, t, setError]);

  const labels = t.dashboard[space];
  const fields = t.dashboard.client;

  // Cascade Pays → Ville → Région (le formulaire stocke le nom du pays)
  const countryCode = useMemo(
    () => allCountries.find((c) => c.name === form.country || c.code === form.country)?.code || '',
    [form.country]
  );
  const villeOptions = useMemo(() => getVilleOptions(countryCode), [countryCode]);
  const regionOptions = useMemo(
    () => getRegionOptions(countryCode, form.city),
    [countryCode, form.city]
  );
  const regionRequired = regionOptions.length > 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!form.civility.trim() || !form.name.trim()) {
      setError(t.auth.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    const result = onSave ? await onSave(form) : await updateProfile(form);
    if (result.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (onBack) setTimeout(onBack, 1200);
    }
  };

  const renderClientForm = () => (
    <ClientRegisterForm
      mode="edit"
      initialValues={user}
      onSubmit={onSave ? (payload) => onSave(payload) : (payload) => updateProfile(payload)}
      successMessage={t.dashboard.saved}
      onSuccess={onBack}
    />
  );

  const renderDefaultForm = () => (
    <form className="dform" onSubmit={onSubmit} noValidate>
      <section className="dform-section">
        <header className="dform-section-head">
          <User size={17} />
          <h4>{labels.profileTitle}</h4>
        </header>
        <div className="dform-grid">
          <div className="dform-field">
            <label htmlFor="prof-civility-default">Civilité <span className="dform-req">*</span></label>
            <select
              id="prof-civility-default"
              required
              value={form.civility}
              onChange={(e) => { setForm({ ...form, civility: e.target.value }); setTouched(true); }}
            >
              <option value="">Sélectionner</option>
              {civilities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="dform-field">
            <label htmlFor="prof-name">
              {fields.fieldName} <span className="dform-req">*</span>
            </label>
            <input
              id="prof-name"
              value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); setTouched(true); }}
              required
            />
          </div>
          <div className="dform-field">
            <label htmlFor="prof-email">{fields.fieldEmail}</label>
            <input id="prof-email" value={user?.email || ''} disabled />
          </div>
          <div className="dform-field">
            <label htmlFor="prof-phone">{fields.fieldPhone}</label>
            <input
              id="prof-phone"
              value={form.phone}
              onChange={(e) => { setForm({ ...form, phone: e.target.value }); setTouched(true); }}
              placeholder="+216 00 000 000"
            />
          </div>
          <div className="dform-field">
            <label htmlFor="prof-city">{fields.fieldCity}</label>
            {villeOptions.length ? (
              <select
                id="prof-city"
                value={form.city}
                onChange={(e) => { setForm({ ...form, city: e.target.value, region: '' }); setTouched(true); }}
              >
                <option value="">Sélectionnez une ville</option>
                {villeOptions.map((v) => (
                  <option key={v.stateCode || v.name} value={v.name}>{v.name}</option>
                ))}
                {form.city && !villeOptions.some((v) => v.name === form.city) && (
                  <option value={form.city}>{form.city}</option>
                )}
              </select>
            ) : (
              <input
                id="prof-city"
                value={form.city}
                onChange={(e) => { setForm({ ...form, city: e.target.value }); setTouched(true); }}
                placeholder="Tunis"
              />
            )}
          </div>
          <div className="dform-field">
            <label htmlFor="prof-region">Région{regionRequired ? ' *' : ''}</label>
            {regionRequired ? (
              <select
                id="prof-region"
                required
                value={form.region}
                onChange={(e) => { setForm({ ...form, region: e.target.value }); setTouched(true); }}
              >
                <option value="">Sélectionnez une région</option>
                {regionOptions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
                {form.region && !regionOptions.includes(form.region) && (
                  <option value={form.region}>{form.region}</option>
                )}
              </select>
            ) : (
              <input
                id="prof-region"
                value={form.region}
                onChange={(e) => { setForm({ ...form, region: e.target.value }); setTouched(true); }}
                placeholder="Ex. La Marsa"
                disabled={villeOptions.length > 0 && !form.city}
              />
            )}
          </div>
        </div>
      </section>
      <footer className="dform-footer">
        <span className="dform-spacer" />
        {error && <p className="auth-error" role="alert">{error}</p>}
        <button type="submit" className="btn btn-primary">
          <Check size={15} /> {t.dashboard.save}
        </button>
      </footer>
      {saved && (
        <div className="dash-toast-pop" role="status">
          <CheckCircle2 size={18} />
          <span>{t.dashboard.saved}</span>
        </div>
      )}
    </form>
  );

  return (
    <>
      <header className="dash-page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1>{labels.profileTitle}</h1>
          <p>{labels.profileSubtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {onBack && (
            <button
              type="button"
              className="btn btn-outline"
              onClick={onBack}
              style={{ background: '#fff', borderColor: 'var(--teal)', color: 'var(--teal)' }}
            >
              {t.dashboard?.back || 'Retour'}
            </button>
          )}
          {!hidePassword && (
            <button type="button" className="btn btn-primary" onClick={() => setPasswordOpen(true)}>
              <Lock size={15} /> {t.auth.changePassword || 'Changer le mot de passe'}
            </button>
          )}
        </div>
      </header>
      <section className="dash-panel">
        {isClient ? renderClientForm() : renderDefaultForm()}
      </section>

      <DashModal
        open={passwordOpen}
        title={t.auth.changePassword || 'Changer le mot de passe'}
        onClose={() => setPasswordOpen(false)}
      >
        <PasswordChangeForm />
      </DashModal>
    </>
  );
}
